/**
 * The one place the legal identity is written down.
 *
 * Every public surface — footers, terms, privacy notice, structured data, the
 * assistant's knowledge, llms.txt — reads from here. Until 16 Sep 2026 the
 * site carried "Kinsiders 777 LLC" on six pages: a misspelling of the
 * registered foreign-language name plus a translated legal form, neither of
 * which appears in the Commercial Register. A name written in one file cannot
 * drift in another.
 *
 * Source: Удостоверение за актуално състояние, Registry Agency, ЕИК 207572707.
 */
export const COMPANY = {
  brand: "Adkiro",
  /**
   * Field 4 of the register entry, "изписване на чужд език". Used exactly as
   * registered; legal forms are not translated (Ltd stays Ltd, as GmbH stays
   * GmbH), which is why this is not "LLC".
   */
  legalName: "Kinsides 777 Ltd",
  legalNameLocal: "КИНСАЙДС 777 ЕООД",
  legalForm: "single-member limited liability company (ЕООД) under Bulgarian law",
  /** ЕИК — the unique identification code in the Bulgarian Commercial Register. */
  uic: "207572707",
  /** VAT number under the Bulgarian VAT Act (ЗДДС); the company is VAT-registered. */
  vat: "BG207572707",
  register: "Commercial Register at the Registry Agency of the Republic of Bulgaria",
  managingDirector: "Kristian Kinchev",
  /** Registered office (седалище и адрес на управление), field 5 of the entry. */
  address: {
    street: "Matochina 13, Dragalevtsi",
    postalCode: "1415",
    city: "Sofia",
    country: "Bulgaria",
    countryCode: "BG",
  },
  email: "kinchev@adkiro.com",
  /**
   * International format, because the buyer is in Dubai or New York and
   * cannot dial a Bulgarian domestic number. Same number as the Google
   * Business Profile — Google matches a business to its website on name,
   * address and phone agreeing, and the site published no phone at all.
   */
  phone: "+359 89 600 0035",
  /** E.164, for tel: links and structured data. */
  phoneE164: "+359896000035",
} as const;

/**
 * Both registered forms of the name, for the site footer.
 *
 * Meta refused business verification on 17 Sep 2026 with: "We can't verify
 * your business website is associated with the business КИНСАЙДС 777 ЕООД
 * because your legal business name must be present on the website." The
 * Cyrillic form was on /terms and /privacy but on no other page, and the
 * name Meta holds on file is the Cyrillic one. It now appears in the footer
 * of every page.
 */
export const COMPANY_LEGAL_FOOTER = `${COMPANY.legalName} (${COMPANY.legalNameLocal})`;

/**
 * Social profile URLs, kept beside COMPANY rather than typed at each call
 * site. The Facebook Page had no clickable link anywhere on the site for
 * three weeks — the URL existed only inside the JSON-LD sameAs array, which
 * search engines read and no visitor ever sees or can click. Every footer
 * now imports both from here.
 */
export const SOCIAL = {
  instagram: "https://www.instagram.com/adkiro.agency",
  facebook: "https://www.facebook.com/profile.php?id=61593858070871",
  /**
   * The company Page, live since 12 Oct 2026. Until then this held Kristian's
   * personal profile and was linked from nowhere a visitor could click.
   */
  linkedin: "https://www.linkedin.com/company/adkiro",
  /** Kristian's own profile. It belongs to the person, not to the company. */
  founderLinkedin: "https://www.linkedin.com/in/kristiankinchev",
  youtube: "https://www.youtube.com/@adkiro.agency",
} as const;

/**
 * WhatsApp is where business in the Gulf starts; a UAE buyer will message
 * long before they fill in a form. Prefilled so the first message is not a
 * blank "hi".
 */
/** A WhatsApp link to Kristian that opens with the given message already written. */
export const whatsappUrl = (message: string) =>
  `https://wa.me/${COMPANY.phoneE164.replace("+", "")}?text=${encodeURIComponent(message)}`;

export const WHATSAPP_URL = whatsappUrl(
  "Hello Kristian, I'd like to talk about Meta advertising for my business.",
);

export const COMPANY_ADDRESS_LINE = `${COMPANY.address.street}, ${COMPANY.address.postalCode} ${COMPANY.address.city}, ${COMPANY.address.country}`;
